import { useMemo } from "react";

import { ListTagsTag, ListTagsTagGroup } from "~frontend/graphql/tags";

export const TAG_SORTS = [
  {
    value: "created_at_desc",
    label: "Created At, Newest First",
  },
  {
    value: "created_at_asc",
    label: "Created At, Oldest First",
  },
  {
    value: "name_asc",
    label: "Name, A-Z",
  },
  {
    value: "name_desc",
    label: "Name, Z-A",
  },
] as const;

type TagSortValue = (typeof TAG_SORTS)[number]["value"];

export function usePaginatedTags<
  T extends ListTagsTag,
  TG extends ListTagsTagGroup,
>({
  tags,
  tagGroups,
  page = 0,
  pageSize,
  search,
  sort = "name_asc",
}: {
  tags: T[];
  tagGroups: TG[];
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: TagSortValue;
}): { data: (T | TG)[]; totalCount: number } {
  return useMemo(() => {
    let combined: (T | TG)[] = [...tags, ...tagGroups];

    if (search) {
      const lowerSearch = search.toLowerCase();
      combined = combined.filter(
        tagOrGroup =>
          tagOrGroup.name.toLocaleLowerCase().includes(lowerSearch) ||
          (tagOrGroup.__typename === "TagGroup" &&
            tagOrGroup.tags.some(tag =>
              tag.name.toLocaleLowerCase().includes(lowerSearch)
            ))
      );
    }

    combined.sort((a, b) => {
      switch (sort) {
        case "created_at_asc":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "created_at_desc":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        case "name_asc":
          return a.name
            .toLocaleLowerCase()
            .localeCompare(b.name.toLocaleLowerCase());
        case "name_desc":
          return b.name
            .toLocaleLowerCase()
            .localeCompare(a.name.toLocaleLowerCase());
      }
    });

    if (!pageSize) {
      return {
        data: combined,
        totalCount: combined.length,
      };
    }

    const startIndex = page * pageSize;
    return {
      data: combined.slice(startIndex, startIndex + pageSize),
      totalCount: combined.length,
    };
  }, [tagGroups, tags, page, pageSize, search, sort]);
}
